import { hasAnyDeliverableItems } from './dispatchTypeLogic';
import moment from 'moment-timezone';
import StatusHelper from '@wix/wixrest-utils/dist/openrest-helpers/Status';
import _ from 'lodash';
import {
  CurbsideInfo,
  DispatchInfo,
  PickupDispatchInfo,
  Restaurant,
} from '../types/Restaurant';
import { DisplayableMenu } from '../types/Menu';
import { DispatchType, VirtualDispatchType } from '../types/Dispatch';
import { isAvailableOn } from 'availability';
import { BusinessNotifications } from '../types/BusinessNotifications';
import { Address } from '../types/Address';
import {
  DEFAULT_ADDRESS,
  DEFAULT_ADDRESS_SHORT,
  NO_ADDRESS,
} from '../constants';
import { getAvailableTimingOptions } from './dateTimeLogic';

interface GetSupportedDispatchTypesArgs {
  deliveryInfos: DispatchInfo[];
  deliveryDisplayableMenu?: DisplayableMenu;
  getVirtualDispatchTypes?: boolean;
  timezone: string;
  isFutureOrdersOnly?: boolean;
}

interface GetDelayMinsArgs {
  deliveryInfos: DispatchInfo[];
  dispatchType: DispatchType;
}

export interface BusinessNotificationDetails {
  notification: BusinessNotifications;
  comment?: string;
}

export function isSupportsFutureOrder(restaurant: Restaurant) {
  return !restaurant.orders.future.disabled;
}

export function getSupportedDispatchTypes({
                                            deliveryInfos,
                                            deliveryDisplayableMenu,
                                            getVirtualDispatchTypes,
                                            timezone,
                                            isFutureOrdersOnly,
                                          }: GetSupportedDispatchTypesArgs): Set<VirtualDispatchType> {
  return deliveryInfos.reduce(
    (deliveryTypes: Set<VirtualDispatchType>, deliveryInfo) => {
      const { status, reason } = StatusHelper.getStatus(
        deliveryInfo.availability,
        moment().tz(timezone),
      );
      const isClosed = status === 'unavailable' && reason === 'closed';

      const foundDeliverableItems =
        !deliveryDisplayableMenu ||
        hasAnyDeliverableItems(deliveryDisplayableMenu);

      if (
        deliveryInfo.type !== 'premises' &&
        !deliveryInfo.inactive &&
        !isClosed &&
        (deliveryInfo.type !== 'delivery' || foundDeliverableItems)
      ) {
        deliveryTypes.add(deliveryInfo.type);
        if (
          getVirtualDispatchTypes &&
          deliveryInfo.type === 'takeout' &&
          StatusHelper.getStatus(
            deliveryInfo.availability,
            moment().tz(timezone),
          ).status!=='unavailable' &&
          deliveryInfo.contactlessDineInInfo &&
          deliveryInfo.contactlessDineInInfo.enabled &&
          !isFutureOrdersOnly
        ) {
          deliveryTypes.add('dine-in');
        }
      }

      return deliveryTypes;
    },
    new Set<DispatchType>(),
  );
}

export function getDelayMins({
                               deliveryInfos,
                               dispatchType,
                             }: GetDelayMinsArgs): number[] {
  const areas = _.filter(
    deliveryInfos,
    (info) => info.type === dispatchType && !info.inactive,
  );
  const delayMins = _.keys(
    _.groupBy(
      _.map(_.map(areas, 'delayMins'), (delayMin) => {
        return delayMin || 0;
      }),
    ),
  );

  return _.map(delayMins, (d) => parseInt(d, 10));
}

export function hasNoAvailableDispatchType(
  restaurant: Restaurant,
  calendar: moment.Moment,
) {
  const isTakeoutAvailable = Boolean(
    restaurant.deliveryInfos.find(
      (di) => di.type === 'takeout' && isAvailableOn(di.availability, calendar),
    ),
  );
  const isTakeoutAvailableAnytime = Boolean(
    restaurant.deliveryInfos.find(
      (di) => di.type === 'takeout' && !di.inactive,
    ),
  );
  const isDeliveryAvailable = Boolean(
    restaurant.deliveryInfos.find(
      (di) =>
        di.type === 'delivery' && isAvailableOn(di.availability, calendar),
    ),
  );
  const isDeliveryAvailableAnytime = Boolean(
    restaurant.deliveryInfos.find((di) => {
      const { status, reason } = StatusHelper.getStatus(
        di.availability,
        moment().tz(restaurant.timezone),
      );
      const isClosed = status === 'unavailable' && reason === 'closed';
      return di.type === 'delivery' && !isClosed;
    }),
  );
  return (
    (!isTakeoutAvailableAnytime && !isDeliveryAvailableAnytime) ||
    (!isTakeoutAvailable &&
      !isDeliveryAvailable &&
      !isSupportsFutureOrder(restaurant))
  );
}

export function getAllBusinessNotifications(
  restaurant: Restaurant,
  hasAnyPaymentMethods: boolean,
  dispatchTime?: number,
): BusinessNotificationDetails[] {
  const calendar = moment(dispatchTime).tz(restaurant.timezone);
  const restStatus = StatusHelper.getStatus(restaurant.openTimes, calendar);
  const notifications = [];

  if (!hasAnyPaymentMethods) {
    notifications.push({
      notification: { notification: BusinessNotifications.NoPaymentMethods },
      weight: 1100,
    });
  }

  if (!isPremiumRestaurant(restaurant)) {
    notifications.push({
      notification: { notification: BusinessNotifications.NotPremium },
      weight: 1000,
    });
  }

  if (isNoPhysicalLocation(restaurant.address)) {
    notifications.push({
      notification: { notification: BusinessNotifications.NoPhysicalLocation },
      weight: 900,
    });
  }

  if (restStatus.status === 'unavailable') {
    const hasAvailableDateAndTimes =
      getAvailableTimingOptions(restaurant, 'delivery').includes('later') ||
      getAvailableTimingOptions(restaurant, 'takeout').includes('later');

    const closedNotifications =
      isSupportsFutureOrder(restaurant) && hasAvailableDateAndTimes
        ? BusinessNotifications.OnlyFutureOrders
        : BusinessNotifications.RestaurantClosed;
    notifications.push({
      notification: { notification: closedNotifications },
      weight: 800,
    });
  }

  const oloStatus = StatusHelper.getStatus(
    _.get(restaurant, 'orders.availability'),
    calendar,
  );

  if (
    oloStatus.status === 'unavailable' ||
    hasNoAvailableDispatchType(restaurant, calendar)
  ) {
    const { locale } = restaurant;
    const comment = _.get(oloStatus, `comment[${locale}]`);
    notifications.push({
      notification: {
        notification: BusinessNotifications.OlOunavailable,
        comment,
      },
      weight: 600,
    });
  }

  notifications.sort((a, b) => b.weight - a.weight);
  return notifications.map((nd) => nd.notification);
}

function isPremiumRestaurant(restaurant: Restaurant) {
  return _.find(restaurant.products, {
    id: 'com.wix.restaurants.orders',
    param: 'premium',
  });
}

export const isNoPhysicalLocation = (address: Address) => {
  return (
    address &&
    (address.formatted === NO_ADDRESS ||
      address.formatted === DEFAULT_ADDRESS ||
      address.formatted === DEFAULT_ADDRESS_SHORT)
  );
};

export const getIsCurbsideEnabled = (
  deliveryInfos: DispatchInfo[],
): boolean => {
  return deliveryInfos.some(
    (deliveryInfo: DispatchInfo) =>
      deliveryInfo.type === 'takeout' && deliveryInfo.curbsideInfo?.enabled,
  );
};

export const getCurbsideInfo = (
  deliveryInfos: DispatchInfo[],
): CurbsideInfo | undefined => {
  const dispatchInfo = deliveryInfos.find(
    (deliveryInfo: DispatchInfo) => deliveryInfo.type === 'takeout',
  );
  const takeoutDispatchInfo = dispatchInfo as PickupDispatchInfo;
  return takeoutDispatchInfo && takeoutDispatchInfo.curbsideInfo?.enabled
    ? takeoutDispatchInfo.curbsideInfo
    : undefined;
};
